import classNames from 'classnames';
import React, { useRef, useState } from 'react';
import Icon, { COLORS, ICONS } from '../icon';
import WaitingIndicator from '../waiting-indicator/waiting-indicator';

interface Props {
	title: string;
	subtitle?: string;
	onClick?: () => void;
	onFile?: (e: File | FileList) => void;
	multiple?: boolean;
	allowedFileTypes?: string[];
	style?: React.CSSProperties;
	uploading?: boolean;
	errorMessage?: string;
}

function bytesToString(bytes: number): string {
	if (bytes > 1024 * 1024 * 1024) {
		return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
	}

	if (bytes > 1024 * 1024) {
		return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
	}

	if (bytes > 1024) {
		return `${(bytes / 1024).toFixed(2)} KB`;
	}

	return `${bytes} B`;
}

export default function LargeButton(props: Props): JSX.Element {
	const {
		title,
		subtitle,
		onClick,
		onFile,
		allowedFileTypes,
		multiple,
		style = {},
		uploading,
		errorMessage,
	} = props;
	const [draggingOver, setDraggingOver] = useState(false);
	const [showTitle, setShowTitle] = useState(title);
	const [showDescription, setShowDescription] = useState(subtitle);
	const inputRef = useRef<HTMLInputElement | null>(null);

	function handleDragOver(e: React.DragEvent<HTMLDivElement>) {
		e.preventDefault();
		e.stopPropagation();
	}

	function handleDragEnter(e: React.DragEvent<HTMLDivElement>) {
		e.preventDefault();
		e.stopPropagation();
		if (onFile) { setDraggingOver(true); }
	}

	function handleDragLeave(e: React.DragEvent<HTMLDivElement>) {
		e.preventDefault();
		e.stopPropagation();
		if (onFile) { setDraggingOver(false); }
	}

	function handleDragDrop(e: React.DragEvent<HTMLDivElement>) {
		e.preventDefault();
		e.stopPropagation();

		if (onFile) {
			const files = e.dataTransfer?.files;
			processFiles(files);
		}
	}

	function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
		if (e.target.files) { processFiles(e.target.files); }
	}

	function processFiles(files: FileList) {
		const file = files?.[0];

		//if they didn't drop anything, leave it
		if (!files || files.length < 1) { return; }

		//if we allow multiple files and they've dropped more than one
		if (multiple && files.length > 1) {
			let isValid = true;
			let invalidFile: File | null = null;
			let totalBytes = 0;

			//if we have clamped down on the allowed types, validate what they dropped
			if (allowedFileTypes) {
				//files have no iterator other than a for loop
				for (let i = 0; i < files.length; ++i) {
					//check if the allowed types have this file's type
					if (!allowedFileTypes.includes(files[i].type)) {
						isValid = false;
						invalidFile = files[i];
						break;
					}
				}

				//if we have an invalid file in the group, show the user an error
				if (!isValid) {
					setShowTitle(
						`File ${invalidFile?.name} is not a valid file type.`
					);
					setShowDescription('Please select a different file.');
					return;
				}
			}

			//there are multiple, get the size of all of them
			for (let i = 0; i < files.length; ++i) {
				totalBytes += files[i].size;
			}

			//show feedback
			setShowTitle(`${files.length} files selected.`);
			setShowDescription(`Total size: ${bytesToString(totalBytes)}`);
			setDraggingOver(false);
			onFile?.(files);

			//return early, skip everything below
			return;
		}

		//if we have just one file
		if (file) {

			//if we require type validation
			if (file.type && allowedFileTypes && !allowedFileTypes.includes(file.type)) {
				setShowTitle(`File ${file.name} is not a valid type.`);
				setShowDescription('Please select a different file.');
			} else {
				setShowTitle(file.name);
				setShowDescription(`Size: ${bytesToString(file.size)}`);
				onFile?.(file);
			}
		}

		setDraggingOver(false);
	}

	function handleClick() {
		inputRef.current?.click();
	}

	return (
		<div
			className={classNames('field-group large-button', {
				'has-subtitle': !!subtitle,
				dragging: draggingOver,
			})}
			onClick={onClick ? onClick : handleClick}
			onDragEnter={handleDragEnter}
			onDragLeave={handleDragLeave}
			onDrop={handleDragDrop}
			onDragEnd={handleDragDrop}
			onDragOver={handleDragOver}
			style={style}
		>
			<label>
				{title === showTitle && (
					<Icon name={ICONS.ADD} size={14} color={COLORS.CYAN} />
				)}{' '}
				{showTitle}
			</label>
			{subtitle && <span>{showDescription}</span>}
			<input
				type="file"
				accept={allowedFileTypes?.join(',') ?? '*'}
				ref={inputRef}
				style={{ display: 'none' }}
				onChange={handleFileChange}
			/>
			{uploading && (
				<div className="uploading">
					<WaitingIndicator />
				</div>
			)}
			{errorMessage && <label className="error-message">{errorMessage}</label>}
		</div>
	);
}
